/**
 * 74.1 (DECISIONS 340), the system in the sky in play: the battle world's system drawn from the ground — the sun as a disc
 * with glare, the other bodies at their true directions as sphere impostors (rendering/systemSky.ts), under the dial the
 * owner picked (Stellaris+ by default; the Lab's row keeps all four and off). Presentation only: it reads the state and the
 * dome, writes neither; the Match never sees it.
 *
 * **The system** comes from the match's map seed: `systemForWorld` (core/systemGen.ts) searches seeded systems for one that
 * holds a battle world of the match planet's archetype at its size (never relabelling a body — a snow world drawn sunward
 * of a desert one is what the owner would notice), with the setup's per-archetype weights (340: "upregulate probability of
 * certain planet types seeding individually").
 *
 * **The frame** is one fixed turn per match from the system's frame into the world: the direction from the battle world to
 * its sun lands on the scene's one sun direction (DECISIONS 140, skyHaze's light), with a seeded roll about it — so every
 * body's direction is `dir.applyQuaternion(frame)` from anywhere on the planet, and the sun in the sky stands where the
 * light that lights the ground comes from. The viewer's up (the horizon) and the day are the dome's (`localUp`,
 * `daylightNow`, `airNow`, `horizonNow`); above the atmosphere there is no horizon and the whole system shows.
 *
 * **The clock** (74.2 step 0, DECISIONS 353; core/skyClock.ts): the sky turns about the planet's spin axis (74.3's
 * `spinAxisOf`) at the body's own day — a rocky world's 10–40 h, a giant's 9–17, a moon's tidally locked month — from a
 * start hour (seeded, or the setup's `skyHour`: the Story's dusk) at one rate for every world (`skyRate`, x12 proposed:
 * a 30-minute match sees six hours of sky; 0 is the static sky), and the bodies run on their rails at true periods. The
 * turned sun goes to the haze each frame (`SkyHaze.setSun`), where the scene's key light, the dome's day, the lamps and the
 * stars read it. Presentation only: the planet's mesh and the sim never turn.
 *
 * **The night sky** (74.7, DECISIONS 352): the system's own star field (core/starField.ts) is drawn by `StarSkyLayer` in
 * the same frame, under the dark night floor, in place of the renderer's random starfield and nebula sphere (stood down
 * while this lives, back when it goes — a classic match or a sky set off keeps them); the shooting stars take their rate
 * and radiants from `meteors` through `SystemSkyPlay.current`.
 *
 * Cost: 2 draws + 2 triangles a body each rendered frame (the layer's), the sky recomputed in a few vector ops a body
 * (`skyFrom`); the star field 2 draws and ~5,800 points, six uniforms; the clock three quaternions and a body's angle each
 * (16–20) a frame — the field turns with the frame; nothing per tick.
 */
import * as THREE from 'three';
import type { GameState, PlanetState } from '../types';
import { systemForWorld, skyFrom, bodyPosition, sizeCategoryOf, SKY_DIALS, DEFAULT_SKY_DIAL, type SkyDialKey, type StarSystem, type SystemBody, type SkyView } from '../core/systemGen';
import { battlePlanetIndex } from '../core/actionSetup';
import { spinAxisOf } from '../core/worldSheet';
import { SKY_RATES, DEFAULT_SKY_RATE, spinPeriodS, orbitPeriodS, startHourOf, hourAngle, systemAt } from '../core/skyClock';
import { skyOrientation, type SkyOrientation } from '../core/obliquity';
import { SystemSkyLayer, horizonDip } from './systemSky';
import { StarSkyLayer } from './starSky';
import { starFieldFor, meteorsFor, NIGHT_FLOOR, type StarField, type MeteorCharacter } from '../core/starField';
import type { SkyHaze } from './skyHaze';

/** How far out (world units) the bodies are drawn: inside the camera's far plane (cameraDepth.ts CAMERA_FAR 500) and the dome (200), beyond anything on the ground. */
export const SKY_DRAW_DISTANCE = 150;
/**
 * Where the sky's layers are drawn this frame (u from the eye): `SKY_DRAW_DISTANCE` inside the air (the ground is nearer and
 * covers what the horizon hides); from space, past the battle world's far limb — on a megaplanet the orbit stop stands farther
 * than 150 u, and layers drawn nearer than the planet pass the depth test over its disc (stars through its night side, a body's
 * disc over the ball) — kept under the far plane. The quads scale with the distance, so nothing else changes.
 */
export function skyDrawDistance(inAir: boolean, distanceToCentre: number, radius: number, far: number): number {
  if (inAir) return SKY_DRAW_DISTANCE;
  return Math.min(far * 0.95, Math.max(SKY_DRAW_DISTANCE, distanceToCentre + radius * 1.05));
}

/** A planet's size category by its radius (SIZE_CATEGORY_RADIUS; the megaplanet counts as massive). */
export { sizeCategoryOf } from '../core/systemGen';   // (74.11: in core now, the match's hour reads it)

/** The dial a config asks for: absent = the owner's default; 'off' = none. */
export function skyDialOf(config: Pick<GameState['config'], 'systemSky'>): SkyDialKey | null {
  const v = config.systemSky ?? DEFAULT_SKY_DIAL;
  return v === 'off' ? null : v;
}

export class SystemSkyPlay {
  readonly layer: SystemSkyLayer;
  /** 74.7 (DECISIONS 352): the system's star field, drawn in place of the renderer's random starfield and nebula (stood down while this lives) */
  readonly stars: StarSkyLayer;
  readonly field: StarField;
  /** 74.7: the meteors of this place — the shooting stars read the rate and the radiants (rendering/shootingStars.ts) */
  readonly meteors: MeteorCharacter;
  /** the live sky of the match, for the shooting stars (null between matches) */
  static current: SystemSkyPlay | null = null;
  private readonly stoodDown: THREE.Object3D[] = [];
  private readonly sunDir = new THREE.Vector3();
  private readonly frame = new THREE.Quaternion();
  private readonly up = new THREE.Vector3();
  private readonly upSys = new THREE.Vector3();
  private readonly inverse = new THREE.Quaternion();
  /** 74.2 step 0 (DECISIONS 353): the sky's clock — the seeded frame at the fixed light, the planet's spin axis (74.3's), the body's day, the start hour, the rate */
  private readonly frame0 = new THREE.Quaternion();
  private readonly axis = new THREE.Vector3(0, 1, 0);
  readonly spinPeriodS: number;
  readonly startHour: number;
  /** sky seconds a match second (0: the static sky 74.1 drew) */
  readonly rate: number;
  /** the first viewer's up (set on the first frame): the hour is measured there, so a pinned dusk is dusk where you stand */
  private up0: THREE.Vector3 | null = null;
  /** sky seconds now; a shot or a sheet may add `timeOffset` (seconds of sky) to look ahead */
  skySeconds = 0;
  timeOffset = 0;
  private readonly sunSys = new THREE.Vector3();
  private readonly sunWorld = new THREE.Vector3();
  /** the polar-dawn fix: the plane's normal and the start's sun about the spin axis (core/obliquity.ts); null with the clock off (the scene's fixed light) */
  readonly orientation: SkyOrientation | null;

  private constructor(private readonly scene: THREE.Scene, readonly system: StarSystem, readonly body: SystemBody, readonly dial: SkyDialKey, readonly planetIndex: number, sun: THREE.Vector3, roll: number, readonly tries: number, readonly exact: boolean, clock: { rate: number; startHour: number; axis: THREE.Vector3; orientation: SkyOrientation | null }) {
    this.layer = new SystemSkyLayer();
    this.layer.setStar(system.star.coreColor, system.star.coronaColor);
    scene.add(this.layer.group);
    this.field = starFieldFor(system);
    this.stars = new StarSkyLayer(this.field);
    scene.add(this.stars.group);
    this.meteors = meteorsFor(system, body);
    this.sunDir.copy(sun).normalize();
    scene.traverse(o => { if (o.userData.starfield && o.visible) { o.visible = false; this.stoodDown.push(o); } });
    SystemSkyPlay.current = this;
    const here = bodyPosition(system, body, SKY_DIALS[dial]);
    const toSun = new THREE.Vector3(-here.x, -here.y, -here.z).normalize();
    if (clock.orientation) {
      // the polar-dawn fix (core/obliquity.ts): the system's plane normal (+y) onto the world's, `obliquity` off the spin axis,
      // then a turn about it that puts the body→sun direction at the season's — the sun's declination at most the obliquity
      const n = new THREE.Vector3(clock.orientation.normal.x, clock.orientation.normal.y, clock.orientation.normal.z).normalize();
      const q1 = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), n);
      const s1 = toSun.clone().applyQuaternion(q1); s1.addScaledVector(n, -s1.dot(n)).normalize();   // the system's sun in the plane (a body's inclination off it dropped)
      const s2 = new THREE.Vector3(clock.orientation.sun.x, clock.orientation.sun.y, clock.orientation.sun.z); s2.addScaledVector(n, -s2.dot(n)).normalize();
      const turn = Math.atan2(new THREE.Vector3().crossVectors(s1, s2).dot(n), s1.dot(s2));
      this.frame.copy(new THREE.Quaternion().setFromAxisAngle(n, turn)).multiply(q1);
    } else {
      // the frame with the clock off (74.1): the body→sun direction (system frame) onto the scene's sun direction (world), then a roll about it
      const align = new THREE.Quaternion().setFromUnitVectors(toSun, sun.clone().normalize());
      const spin = new THREE.Quaternion().setFromAxisAngle(sun.clone().normalize(), roll);
      this.frame.copy(spin).multiply(align);
    }
    this.orientation = clock.orientation;
    this.inverse.copy(this.frame).invert();
    this.frame0.copy(this.frame);
    this.axis.copy(clock.axis).normalize();
    this.rate = clock.rate; this.startHour = clock.startHour;
    this.spinPeriodS = spinPeriodS(system, body);
    this.sunWorld.copy(sun).normalize();
  }

  /** The sky for a match, or null when the config says off or no planet is fought on. */
  static forMatch(scene: THREE.Scene, state: GameState, sun: THREE.Vector3): SystemSkyPlay | null {
    const dial = skyDialOf(state.config);
    if (!dial) return null;
    const planetIndex = battlePlanetIndex(state);
    const planet = state.planets[planetIndex];
    if (!planet) return null;
    const seed = state.config.seed;
    const found = systemForWorld(seed, planet.archetype, sizeCategoryOf(planet.radius), { archetypeWeights: state.config.skyWeights });
    // the roll: a seeded angle, so the same match shows the same sky
    const roll = (hashOf(`${seed}-sky-roll`) % 3600) / 3600 * Math.PI * 2;
    // 74.2 step 0: the clock — the rate (the setup's, else the proposed default), the start hour (pinned, else seeded), the planet's spin axis
    const rate = state.config.skyRate ?? SKY_RATES[DEFAULT_SKY_RATE];
    const startHour = state.config.skyHour ?? startHourOf(seed);
    const a = planet.heightmap ? spinAxisOf(planet) : { x: 0, y: 1, z: 0 };   // (a test's bare planet: the world's up)
    // the polar-dawn fix: with the clock running, the plane and the start's sun stand about the axis with a bounded obliquity (core/obliquity.ts); off, the scene's fixed light as 74.1 drew it
    const orientation = rate > 0 ? skyOrientation(seed, a, found.body.kind, found.body.id) : null;
    return new SystemSkyPlay(scene, found.system, found.body, dial, planetIndex, sun, roll, found.tries, found.exact, { rate, startHour, axis: new THREE.Vector3(a.x, a.y, a.z), orientation });
  }

  /** Place, size and fade every body and the sun for this frame, against the dome's day at the camera. */
  update(camera: THREE.PerspectiveCamera, haze: SkyHaze, heightPx: number, planets: readonly PlanetState[], matchSeconds = 0): void {
    const planet = planets[this.planetIndex];
    // the horizon: the dome's local up inside the air; otherwise the radial from the battle planet (space, or an airless world)
    if (haze.localUp) this.up.copy(haze.localUp);
    else if (planet) this.up.copy(camera.position).sub(new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z)).normalize();
    else this.up.set(0, 1, 0);
    // 74.8: above the air the horizon dips with height — a body stands in the sky until the planet's disc covers it (the overview, the orbit); the rule is on elevations against `up`, so it holds under 74.2's turning frame
    this.layer.horizon = planet && !haze.localUp ? horizonDip(planet.radius, Math.hypot(camera.position.x - planet.position.x, camera.position.y - planet.position.y, camera.position.z - planet.position.z)) : -0.02;
    // 74.2 step 0: the clock turns the sky about the planet's axis and moves the bodies on their rails; the haze's sun follows (read by the dome next frame)
    const system = this.rate > 0 ? this.turn(matchSeconds, haze) : this.system;
    this.upSys.copy(this.up).applyQuaternion(this.inverse);
    const sky = skyFrom(system, system.bodies[this.body.id], { x: this.upSys.x, y: this.upSys.y, z: this.upSys.z }, SKY_DIALS[this.dial]);   // (the viewer's own world advanced on its rail too — `this.body` is the seeded one; the sun's direction was read from the advanced one already, the other bodies' were not)
    const distance = planet ? skyDrawDistance(!!haze.localUp, Math.hypot(camera.position.x - planet.position.x, camera.position.y - planet.position.y, camera.position.z - planet.position.z), planet.radius, camera.far) : SKY_DRAW_DISTANCE;
    this.layer.update({ camera, heightPx, eye: camera.position, frame: this.frame, distance, up: this.up, sky, daylight: haze.daylightNow, air: haze.airNow, skyColor: haze.horizonNow, sunTint: haze.sunTint });
    this.stars.update({ camera, eye: camera.position, frame: this.frame, distance, daylight: haze.daylightNow, air: haze.airNow, floor: NIGHT_FLOOR.proposed, sunDir: this.sunWorld, sunRadius: sky.sun.drawnRadius, skyColor: haze.horizonNow, up: haze.localUp, density: haze.densityNow ?? 0, pixelRatio: Math.min((globalThis as { devicePixelRatio?: number }).devicePixelRatio ?? 1, 2) });
  }

  /**
   * The sky at `matchSeconds`: the system's bodies advanced on their rails, and the frame turned about the spin axis so the sun
   * stands at the hour the clock says — the start hour measured for the first viewer (so a pinned dusk is dusk where you
   * stand), then on at the rate. The turned sun goes to the haze (one setter: the scene's light, the dome, the lamps, the
   * stars read it). Returns the time-shifted system.
   */
  private turn(matchSeconds: number, haze: SkyHaze): StarSystem {
    this.skySeconds = matchSeconds * this.rate + this.timeOffset;
    const system = systemAt(this.system, this.skySeconds);
    const here = bodyPosition(system, system.bodies[this.body.id], SKY_DIALS[this.dial]);
    this.sunSys.set(-here.x, -here.y, -here.z).normalize();
    if (this.up0 === null) this.up0 = this.up.clone();
    this.frameAt(this.skySeconds, system, this.frame);
    this.inverse.copy(this.frame).invert();
    this.sunWorld.copy(this.sunSys).applyQuaternion(this.frame);
    haze.setSun(this.sunWorld);
    return system;
  }
  /**
   * The frame (system → world) at `skySeconds`: the seeded frame turned about the spin axis so the sun stands at the hour the
   * clock says there (the sun's hour angle at the first viewer is the clock's, exactly; under the dial a moon's sun swings a
   * little with its month). The seeded frame with the clock off or before the first frame.
   */
  frameAt(skySeconds: number, system: StarSystem = this.rate > 0 ? systemAt(this.system, skySeconds) : this.system, out = new THREE.Quaternion()): THREE.Quaternion {
    if (this.rate <= 0 || this.up0 === null) return out.copy(this.frame);
    const here = bodyPosition(system, system.bodies[this.body.id], SKY_DIALS[this.dial]);
    const s0 = V4.set(-here.x, -here.y, -here.z).normalize().applyQuaternion(this.frame0);   // the sun at the seeded frame, then
    const target = hourAngle(skySeconds, this.startHour, this.spinPeriodS);
    return out.copy(Q2.setFromAxisAngle(this.axis, target - this.hourAngleOf(s0, this.up0))).multiply(this.frame0);
  }
  /**
   * 74.9 (the system stop's sheet): where a body stands in the world at `skySeconds` of the clock — its rail and the spin
   * together, as the sky will draw it then — without moving the sky. null for no such body.
   */
  bodyDirectionAt(bodyId: number, skySeconds: number, out = new THREE.Vector3()): THREE.Vector3 | null {
    const system = this.rate > 0 ? systemAt(this.system, skySeconds) : this.system;
    if (!system.bodies[bodyId] || bodyId === this.body.id) return null;
    const frame = this.frameAt(skySeconds, system, Q3);
    const upSys = V5.copy(this.up).applyQuaternion(Q4.copy(frame).invert());
    const v = skyFrom(system, system.bodies[this.body.id], { x: upSys.x, y: upSys.y, z: upSys.z }, SKY_DIALS[this.dial]).bodies.find(b => b.body !== null && b.body.id === bodyId);
    return v ? out.set(v.dir.x, v.dir.y, v.dir.z).applyQuaternion(frame) : null;
  }
  /** 74.9 (367): the biggest body in the sky now (by drawn size) and where it stands in the world — the system stop centres its frame on it; null with no body. */
  biggestBody(out = new THREE.Vector3()): { name: string; dir: THREE.Vector3; drawnRadius: number } | null {
    const system = this.rate > 0 ? systemAt(this.system, this.skySeconds) : this.system;
    let best: SkyView | null = null;
    for (const v of skyFrom(system, system.bodies[this.body.id], { x: this.upSys.x, y: this.upSys.y, z: this.upSys.z }, SKY_DIALS[this.dial]).bodies) if (v.body !== null && (!best || v.drawnRadius > best.drawnRadius)) best = v;
    if (!best || !best.body) return null;
    return { name: best.body.name, dir: out.set(best.dir.x, best.dir.y, best.dir.z).applyQuaternion(this.frame), drawnRadius: best.drawnRadius };
  }
  /** 74.9: the system's plane's normal in the world this frame — the rails lie in the plane (a moon's within its tilt). */
  planeNormal(out = new THREE.Vector3()): THREE.Vector3 { return out.set(0, 1, 0).applyQuaternion(this.frame); }
  /** The sun's hour angle about the axis as seen from `up`: 0 at the viewer's noon, growing with the day. */
  private hourAngleOf(sun: THREE.Vector3, up: THREE.Vector3): number {
    const m = V2.copy(up).addScaledVector(this.axis, -up.dot(this.axis));
    if (m.lengthSq() < 1e-9) m.set(1, 0, 0).addScaledVector(this.axis, -this.axis.x).normalize(); else m.normalize();
    const e = V3.crossVectors(this.axis, m);
    return Math.atan2(sun.dot(e), sun.dot(m));
  }
  /** The clock for the Lab and a shot: the body's day and year, the rate, the hour now (a fraction of the day). */
  clock(): { dayH: number; yearD: number; locked: boolean; rate: number; startHour: number; skySeconds: number; hourNow: number } {
    const h = this.startHour + this.skySeconds / this.spinPeriodS;
    return { dayH: this.spinPeriodS / 3600, yearD: orbitPeriodS(this.system, this.body) / 86400, locked: this.body.parent !== null, rate: this.rate, startHour: this.startHour, skySeconds: this.skySeconds, hourNow: h - Math.floor(h) };
  }
  /** The sun's direction in the world this frame (the haze's, when the clock runs). */
  get sunDirection(): THREE.Vector3 { return this.sunWorld; }
  /** The meteors' radiants in the world (unit directions), for the showers. */
  radiantsWorld(): THREE.Vector3[] { return this.meteors.radiants.map(r => new THREE.Vector3(r.dir.x, r.dir.y, r.dir.z).applyQuaternion(this.frame)); }
  /** The star field's draws, points and this frame's limit. */
  starStats(): ReturnType<StarSkyLayer['stats']> { return this.stars.stats(); }

  /** This frame's draws and triangles (decision 136). */
  stats(): ReturnType<SystemSkyLayer['stats']> { return this.layer.stats(); }

  dispose(): void {
    this.scene.remove(this.layer.group);
    this.layer.dispose();
    this.scene.remove(this.stars.group);
    this.stars.dispose();
    for (const o of this.stoodDown) o.visible = true;
    this.stoodDown.length = 0;
    if (SystemSkyPlay.current === this) SystemSkyPlay.current = null;
  }
}

const V2 = new THREE.Vector3(), V3 = new THREE.Vector3(), V4 = new THREE.Vector3(), V5 = new THREE.Vector3(), Q2 = new THREE.Quaternion(), Q3 = new THREE.Quaternion(), Q4 = new THREE.Quaternion();

/** A small string hash (FNV-1a), for the roll. */
function hashOf(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return h >>> 0;
}
